

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserProfile } from '../../../types';
import { Sparkles, Loader2, Square, CheckSquare, FileText, Send, Bot, Wand2, ArrowRight, X, Trash2, Edit3, Calendar, Image as ImageIcon, RefreshCw, AlertCircle } from 'lucide-react';

// --- TYPES ---
interface Topic {
  id: number;
  title: string;
  checked: boolean;
}

interface TopicPayload {
    title: string;
    categoryId: number;
}

interface ArticleToProcess {
    id: number;
    title: string;
    categoryId: number;
    checked: boolean;
    content: string | null;
    status: 'pending' | 'generating' | 'saving' | 'done' | 'error';
    error?: string;
}

interface ArticleForImage {
    id: number;
    title: string;
    prompt: string;
    status: 'pending' | 'generating_prompt' | 'generating_image' | 'done' | 'error';
    checked: boolean;
    error?: string;
}

interface BlogCategory {
    id: number;
    name: string;
    parent_id: number | null;
}

interface WakeLockSentinel {
    release(): Promise<void>;
    readonly released: boolean;
    onrelease: (() => void) | null;
}

interface NavigatorWithWakeLock extends Navigator {
    wakeLock: {
        request(type: 'screen'): Promise<WakeLockSentinel>;
    };
}

interface Props {
  currentUser: UserProfile;
}

// --- HELPER: Slugify ---
const slugify = (str: string) => {
    str = str.replace(/^\s+|\s+$/g, ''); // trim
    str = str.toLowerCase();

    const charMap: { [key: string]: string } = {
        'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ż': 'z', 'ź': 'z',
        'à': 'a', 'á': 'a', 'ä': 'a', 'â': 'a', 'ã': 'a', 'å': 'a', 'æ': 'a',
        'è': 'e', 'é': 'e', 'ë': 'e', 'ê': 'e',
        'ì': 'i', 'í': 'i', 'ï': 'i', 'î': 'i',
        'ñ': 'n',
        'ò': 'o', 'ö': 'o', 'ô': 'o', 'õ': 'o', 'ø': 'o',
        'š': 's',
        'ù': 'u', 'ú': 'u', 'ü': 'u', 'û': 'u',
        'ç': 'c',
        '·': '-', '/': '-', '_': '-', ',': '-', ':': '-', ';': '-'
    };
    
    // Create a regex from the map keys, escaping special regex characters
    const regex = new RegExp(Object.keys(charMap).map(c => c.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')).join('|'), 'g');
    
    str = str.replace(regex, (matched) => charMap[matched as keyof typeof charMap]);

    str = str.replace(/[^a-z0-9 -]/g, '') // remove invalid chars that were not in the map
        .replace(/\s+/g, '-') // collapse whitespace and replace by -
        .replace(/-+/g, '-'); // collapse dashes

    return str;
};


// --- SUB-COMPONENT: Topics View ---
const TopicsGenerator: React.FC<{ onTopicsSelected: (topics: TopicPayload[]) => void }> = ({ onTopicsSelected }) => {
    const [prompt, setPrompt] = useState('świece sojowe i ich zalety');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [generatedTopics, setGeneratedTopics] = useState<Topic[]>([]);
    const [categories, setCategories] = useState<BlogCategory[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

    useEffect(() => {
        const fetchCats = async () => {
            try {
                const res = await fetch('/api/cms/blog-categories');
                if (res.ok) {
                    setCategories(await res.json());
                }
            } catch (_e) {
                console.error("Failed to fetch categories", _e);
            }
        };
        fetchCats();
    }, []);

    const hierarchicalCategories = useMemo(() => {
        const sorted: (BlogCategory & { level: number })[] = [];
        const buildTree = (parentId: number | null, level: number) => {
            const children = categories
                .filter(c => c.parent_id === parentId)
                .sort((a, b) => a.name.localeCompare(b.name));

            children.forEach(cat => {
                sorted.push({ ...cat, level });
                buildTree(cat.id, level + 1);
            });
        };
        buildTree(null, 0);
        return sorted;
    }, [categories]);

    const handleGenerateTopics = async () => {
        if (!prompt || !selectedCategoryId) {
            setError("Please select a category and provide a topic.");
            return;
        }
        setIsLoading(true);
        setError(null);
        setGeneratedTopics([]);

        try {
            // Get existing topics from queue to avoid duplicates
            const storedTopicsRaw = sessionStorage.getItem('ai_topics_to_generate');
            const existingTopics: TopicPayload[] = storedTopicsRaw ? JSON.parse(storedTopicsRaw) : [];
            const exclude_titles = existingTopics.map(t => t.title);

            const res = await fetch('/api/cms/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'topics',
                    prompt,
                    exclude_titles
                })
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || "Failed to generate topics from API.");
            }
            
            const parsed = await res.json();
            
            if (parsed.titles && Array.isArray(parsed.titles)) {
                setGeneratedTopics(parsed.titles.map((title: string, index: number) => ({ id: index, title, checked: true })));
            } else {
                throw new Error("Invalid response format from AI.");
            }

        } catch (e: unknown) {
            console.error(e);
            const message = e instanceof Error ? e.message : "Failed to generate topics.";
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleTopicCheck = (id: number) => {
        setGeneratedTopics(prev => prev.map(topic => topic.id === id ? { ...topic, checked: !topic.checked } : topic));
    };

    const selectedCount = useMemo(() => generatedTopics.filter(t => t.checked).length, [generatedTopics]);

    const handleProceed = () => {
        if (!selectedCategoryId) {
            setError('Please select a category first.');
            return;
        }
        const selected = generatedTopics
            .filter(t => t.checked)
            .map(t => ({ title: t.title, categoryId: selectedCategoryId }));
        onTopicsSelected(selected);
    };

    return (
        <div className="grid grid-cols-12 gap-6 h-full">
            {/* Left Column: Results */}
            <div className="col-span-7 bg-mystic-box border border-gray-800 rounded-lg p-6 flex flex-col">
                <h3 className="text-gray-400 font-bold text-sm mb-4">Generated Topics ({generatedTopics.length})</h3>
                <div className="flex-grow overflow-y-auto pr-2">
                    {isLoading && <div className="flex items-center justify-center h-full text-gray-500"><Loader2 className="animate-spin w-8 h-8"/></div>}
                    {!isLoading && generatedTopics.length === 0 && (
                        <div className="flex items-center justify-center h-full text-center text-gray-600">
                           <div className="flex flex-col items-center gap-2">
                               <Bot size={48} className="opacity-30" />
                               <span>Results will appear here.</span>
                           </div>
                        </div>
                    )}
                    {generatedTopics.length > 0 && (
                        <ul className="space-y-3">
                            {generatedTopics.map(topic => (
                                <li key={topic.id} onClick={() => handleTopicCheck(topic.id)} className="flex items-center gap-3 p-3 bg-mystic-bg rounded-md border border-gray-700 cursor-pointer hover:bg-mystic-btnHover transition-colors">
                                    {topic.checked ? <CheckSquare className="w-5 h-5 text-mystic-accent shrink-0"/> : <Square className="w-5 h-5 text-gray-600 shrink-0"/>}
                                    <span className="text-gray-200">{topic.title}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {generatedTopics.length > 0 && (
                    <div className="mt-6 border-t border-gray-800 pt-4 flex justify-end">
                        <button 
                            onClick={handleProceed}
                            disabled={selectedCount === 0}
                            className="bg-mystic-accent text-black font-bold px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-mystic-accentHover"
                        >
                           Generate {selectedCount} Article{selectedCount > 1 ? 's' : ''} <ArrowRight size={16}/>
                        </button>
                    </div>
                )}
            </div>

            {/* Right Column: Generator */}
            <div className="col-span-5 bg-mystic-box border border-gray-800 rounded-lg p-6 flex flex-col">
                <h3 className="text-gray-400 font-bold text-sm mb-4 flex items-center gap-2"><Wand2 size={16} /> AI Title Generator</h3>
                
                <label className="text-xs text-gray-500 font-bold uppercase mb-2">Category</label>
                <select
                    value={selectedCategoryId ?? ''}
                    onChange={(e) => setSelectedCategoryId(Number(e.target.value) || null)}
                    className="w-full bg-mystic-inset border border-gray-700 rounded-md p-3 text-gray-200 focus:border-mystic-accent outline-none mb-4"
                >
                    <option value="">-- Select a Category --</option>
                    {hierarchicalCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                            {'\u00A0\u00A0'.repeat(cat.level)}{cat.level > 0 ? '— ' : ''}{cat.name}
                        </option>
                    ))}
                </select>

                <label className="text-xs text-gray-500 font-bold uppercase mb-2">Topic / Keywords</label>
                <textarea 
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., Sustainable gardening tips for beginners..."
                    className="w-full h-40 bg-mystic-inset border border-gray-700 rounded-md p-3 text-gray-200 resize-none focus:border-mystic-accent outline-none"
                />
                <button 
                    onClick={handleGenerateTopics}
                    disabled={isLoading || !prompt || !selectedCategoryId}
                    className="w-full mt-auto bg-mystic-btn hover:bg-white/90 text-white hover:text-black font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
                >
                    {isLoading ? <Loader2 className="animate-spin"/> : <Sparkles size={16}/>}
                    Generate Topics
                </button>
                 {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
            </div>
        </div>
    );
};

// --- SUB-COMPONENT: Articles View ---
const ArticlesGenerator: React.FC<{ topics: TopicPayload[], currentUser: UserProfile, onBack: () => void }> = ({ topics, currentUser, onBack }) => {
    const [articles, setArticles] = useState<ArticleToProcess[]>([]);
    const [categories, setCategories] = useState<BlogCategory[]>([]);
    const [minChars, setMinChars] = useState(2000);
    const [maxChars, setMaxChars] = useState(4000);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [isQueueRunning, setIsQueueRunning] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);
    const wakeLock = useRef<WakeLockSentinel | null>(null); // Use 'any' for WakeLockSentinel for broader compatibility

    // --- Wake Lock ---
    const acquireWakeLock = async () => {
        if ('wakeLock' in navigator) {
            try {
                wakeLock.current = await (navigator as NavigatorWithWakeLock).wakeLock.request('screen');
                console.log('Wake Lock is active!');
            } catch (err) {
                console.error(`${err.name}, ${err.message}`);
            }
        } else {
            console.warn('Wake Lock API not supported.');
        }
    };
    const releaseWakeLock = async () => {
        if (wakeLock.current) {
            await wakeLock.current.release();
            wakeLock.current = null;
            console.log('Wake Lock released.');
        }
    };

    // Release wake lock on component unmount
    useEffect(() => {
        return () => {
            releaseWakeLock();
        };
    }, []);

    // Fetch categories for display
    useEffect(() => {
        const fetchCats = async () => {
            try {
                const res = await fetch('/api/cms/blog-categories');
                if (res.ok) setCategories(await res.json());
            } catch (_e) { console.error("Failed to fetch categories", _e); }
        };
        fetchCats();
    }, []);

    // Initialize articles from topics
    useEffect(() => {
        if (topics.length > 0) {
            setArticles(topics.map((topic, index) => ({ 
                id: index,
                title: topic.title, 
                categoryId: topic.categoryId,
                checked: true,
                content: null, 
                status: 'pending' 
            })));
        } else {
            setArticles([]);
        }
    }, [topics]);

    // Initialize date range to last 7 days
    useEffect(() => {
        const today = new Date();
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(today.getDate() - 7);
        const formatDate = (d: Date) => d.toISOString().split('T')[0];
        setDateTo(formatDate(today));
        setDateFrom(formatDate(sevenDaysAgo));
    }, []);
    
    const getAncestorIds = (childId: number, allCategories: BlogCategory[]): number[] => {
        const ancestors: number[] = [];
        let currentCat = allCategories.find(c => c.id === childId);
        while (currentCat && currentCat.parent_id) {
            const parentId = currentCat.parent_id;
            if (!ancestors.includes(parentId)) { // Avoid infinite loops with bad data
                ancestors.push(parentId);
                currentCat = allCategories.find(c => c.id === parentId);
            } else {
                break;
            }
        }
        return ancestors;
    };

    const getCategoryHierarchy = (childId: number, allCategories: BlogCategory[]): string => {
        const path: string[] = [];
        let currentCat = allCategories.find(c => c.id === childId);
        
        while (currentCat) {
            path.unshift(currentCat.name);
            if (currentCat.parent_id) {
                // Determine next parent
                const parentId = currentCat.parent_id;
                currentCat = allCategories.find(c => c.id === parentId);
            } else {
                currentCat = undefined;
            }
        }
        return path.join(' > ');
    };

    const handleToggleCheck = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newArticles = [...articles];
        const currentIndex = newArticles.findIndex(a => a.id === id);
        
        if (e.shiftKey && lastSelectedId !== null) {
            const lastIndex = newArticles.findIndex(a => a.id === lastSelectedId);
            const start = Math.min(currentIndex, lastIndex);
            const end = Math.max(currentIndex, lastIndex);
            const isChecking = !newArticles[currentIndex].checked;
            for (let i = start; i <= end; i++) {
                newArticles[i].checked = isChecking;
            }
        } else {
            newArticles[currentIndex].checked = !newArticles[currentIndex].checked;
        }

        setArticles(newArticles);
        setLastSelectedId(id);
    };

    const handleRemoveArticle = (id: number) => {
        setArticles(prev => prev.filter(a => a.id !== id));
    };
    
    const handleToggleAll = () => {
        const areAllChecked = articles.every(a => a.checked);
        setArticles(prev => prev.map(a => ({ ...a, checked: !areAllChecked })));
    };

    const handleStartEditing = (article: ArticleToProcess) => {
        setEditingId(article.id);
        setEditingTitle(article.title);
    };

    const handleSaveTitle = () => {
        if (editingId !== null) {
            setArticles(prev => prev.map(a => a.id === editingId ? { ...a, title: editingTitle } : a));
        }
        setEditingId(null);
        setEditingTitle('');
    };

    const generateAndSaveSingleArticle = async (article: ArticleToProcess) => {
        if (!article || (article.status !== 'pending' && article.status !== 'error')) return;
        
        setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: 'generating', error: undefined } : a));

        try {
            // Build context string from category
            const categoryContext = getCategoryHierarchy(article.categoryId, categories);

            const res = await fetch('/api/cms/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    type: 'article', 
                    title: article.title, 
                    minChars, 
                    maxChars,
                    categoryContext // Pass category hierarchy
                })
            });
            
            if (!res.ok) throw new Error((await res.json()).error || "API Error");

            const { htmlContent, summary } = await res.json();
            if (!htmlContent || !summary) throw new Error("Invalid AI response.");
            
            setArticles(prev => prev.map(a => a.id === article.id ? { ...a, content: htmlContent, status: 'saving' } : a));
            await saveArticleToDb(article.title, htmlContent, summary, article.categoryId);
            setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: 'done' } : a));

        } catch (e: unknown) {
            console.error(e);
            const message = e instanceof Error ? e.message : 'An unknown error occurred.';
            setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: 'error', error: message } : a));
        }
    };

    const startGenerationQueue = async () => {
        setIsQueueRunning(true);
        await acquireWakeLock();
        try {
            const articlesToProcess = articles.filter(a => a.checked && (a.status === 'pending' || a.status === 'error'));
            for (const article of articlesToProcess) {
                await generateAndSaveSingleArticle(article);
            }
        } finally {
            setIsQueueRunning(false);
            await releaseWakeLock();
        }
    };

    const handleRetryArticle = (articleId: number) => {
        const articleToRetry = articles.find(a => a.id === articleId);
        if (articleToRetry) {
            generateAndSaveSingleArticle(articleToRetry);
        }
    };
    
    const saveArticleToDb = async (title: string, content_html: string, summary: string, categoryId: number) => {
        const fromTime = new Date(dateFrom).getTime();
        const toDate = new Date(dateTo);
        toDate.setDate(toDate.getDate() + 1);
        const toTime = toDate.getTime();
        const randomTime = fromTime + Math.random() * (toTime - fromTime);
        
        const ancestorIds = getAncestorIds(categoryId, categories);
        const allCategoryIds = Array.from(new Set([categoryId, ...ancestorIds]));

        const payload = {
            title, slug: slugify(title), content_html, summary,
            status: 'published', author_id: currentUser.id, published_at: randomTime,
            categories: allCategoryIds
        };

        const res = await fetch('/api/cms/blog', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
            body: JSON.stringify(payload)
        });

        if (!res.ok) throw new Error((await res.json()).error || 'DB save failed.');
    };

    const articlesInQueue = useMemo(() => articles.filter(a => a.checked), [articles]);
    const articlesToGenerate = useMemo(() => articles.filter(a => a.checked && (a.status === 'pending' || a.status === 'error')), [articles]);
    const doneCount = useMemo(() => articles.filter(a => a.status === 'done').length, [articles]);
    const errorCount = useMemo(() => articles.filter(a => a.status === 'error').length, [articles]);
    const progressPercentage = articles.length > 0 ? ((doneCount / articles.length) * 100) : 0;

    return (
        <div className="grid grid-cols-12 gap-6 h-full">
            {/* Left Column: Article List */}
            <div className="col-span-7 bg-mystic-box border border-gray-800 rounded-lg p-6 flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-gray-400 font-bold text-sm">Articles in Queue ({articles.length})</h3>
                    <div className="flex items-center gap-4">
                       <button onClick={handleToggleAll} className="text-xs text-gray-500 hover:text-white">
                         {articles.every(a => a.checked) ? 'Deselect All' : 'Select All'}
                       </button>
                       <button onClick={() => {
                           setArticles([]);
                           sessionStorage.removeItem('ai_topics_to_generate');
                       }} className="text-xs text-red-500 hover:text-red-400 flex items-center gap-1">
                         <Trash2 size={12}/> Clear List
                       </button>
                       <button onClick={onBack} className="text-xs text-gray-500 hover:text-white">&larr; Back to topics</button>
                    </div>
                </div>

                <div className="mb-4">
                    <div className="w-full bg-gray-800 rounded-full h-1.5">
                        <div className="bg-green-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${progressPercentage}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                        <span>Completed: {doneCount} / {articles.length}</span>
                        <span>Checked: {articlesInQueue.length}</span>
                        {errorCount > 0 && <span className="text-red-400 font-bold">Errors: {errorCount}</span>}
                        {isQueueRunning && <span className="text-blue-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> Running...</span>}
                    </div>
                </div>

                <ul className="space-y-2 overflow-y-auto pr-2 flex-grow">
                    {articles.map(article => {
                        const categoryName = categories.find(c => c.id === article.categoryId)?.name || '...';
                        return (
                            <li key={article.id} className="p-2 rounded-md border flex flex-col gap-2 bg-mystic-bg border-gray-700">
                               <div className="flex items-center gap-3">
                                   <div onClick={(e) => handleToggleCheck(article.id, e)} className="cursor-pointer p-1">
                                     {article.checked ? <CheckSquare className="w-5 h-5 text-mystic-accent"/> : <Square className="w-5 h-5 text-gray-600"/>}
                                   </div>
                                   
                                   <div className="flex-1 min-w-0">
                                        {editingId === article.id ? (
                                            <input
                                                type="text"
                                                value={editingTitle}
                                                onChange={e => setEditingTitle(e.target.value)}
                                                onBlur={handleSaveTitle}
                                                onKeyDown={e => e.key === 'Enter' && handleSaveTitle()}
                                                className="w-full bg-transparent border-b border-mystic-accent text-white outline-none"
                                                autoFocus
                                            />
                                        ) : (
                                            <p onClick={() => handleStartEditing(article)} className="truncate cursor-pointer hover:text-mystic-accent text-sm text-white">{article.title}</p>
                                        )}
                                         <span className="text-[10px] bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded-full mt-1 inline-block">{categoryName}</span>
                                   </div>

                                   <div className="flex items-center gap-2">
                                        <span className={`text-xs w-20 text-center font-bold uppercase ${article.status === 'generating' || article.status === 'saving' ? 'text-blue-400' : article.status === 'done' ? 'text-green-400' : article.status === 'error' ? 'text-red-400' : 'text-gray-500'}`}>
                                            {article.status}
                                        </span>
                                        <button onClick={() => handleStartEditing(article)} className="text-gray-600 hover:text-white"><Edit3 size={14}/></button>
                                        <button onClick={() => handleRemoveArticle(article.id)} className="text-gray-600 hover:text-red-500"><X size={16}/></button>
                                   </div>
                               </div>
                               {article.status === 'error' && (
                                   <div className="text-xs text-red-300 bg-red-900/20 p-2 rounded-md flex items-center justify-between gap-2 ml-9">
                                       <div className="flex items-start gap-1.5">
                                           <AlertCircle size={14} className="shrink-0 mt-0.5" />
                                           <span className="break-all">{article.error}</span>
                                       </div>
                                       <button
                                           onClick={() => handleRetryArticle(article.id)}
                                           disabled={isQueueRunning}
                                           className="flex items-center gap-1 text-white bg-red-500/50 px-2 py-1 rounded hover:bg-red-500 disabled:opacity-50"
                                       >
                                           <RefreshCw size={12} /> Retry
                                       </button>
                                   </div>
                               )}
                            </li>
                        );
                    })}
                </ul>
            </div>
            {/* Right Column: AI Writer */}
            <div className="col-span-5 bg-mystic-box border border-gray-800 rounded-lg p-6 flex flex-col">
                <h3 className="text-gray-400 font-bold text-sm mb-4 flex items-center gap-2"><Sparkles size={16}/> AI Writer Settings</h3>

                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="text-xs text-gray-500 font-bold uppercase mb-2">Min Characters</label>
                        <input type="number" value={minChars} onChange={e => setMinChars(Number(e.target.value))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white"/>
                    </div>
                     <div>
                        <label className="text-xs text-gray-500 font-bold uppercase mb-2">Max Characters</label>
                        <input type="number" value={maxChars} onChange={e => setMaxChars(Number(e.target.value))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white"/>
                    </div>
                </div>

                <div className="p-4 bg-black/30 rounded-lg border border-gray-700 mb-6">
                    <label className="text-xs text-gray-500 font-bold uppercase mb-2 flex items-center gap-1"><Calendar size={12}/> Publication Date Range</label>
                     <div className="grid grid-cols-2 gap-4">
                        <div>
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white text-xs"/>
                        </div>
                         <div>
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white text-xs"/>
                        </div>
                    </div>
                </div>
                
                <div className="bg-blue-900/20 border border-blue-800 text-blue-200 text-xs p-3 rounded-md mb-4">
                    <strong>Note:</strong> Articles will be generated sequentially and saved as 'published' with a random date from the selected range.
                </div>

                <button 
                    onClick={startGenerationQueue}
                    disabled={isQueueRunning || articlesToGenerate.length === 0}
                    className="w-full mt-auto bg-mystic-accent hover:bg-mystic-accentHover text-black font-bold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                     {isQueueRunning ? <Loader2 className="animate-spin"/> : <Send size={16}/>}
                     {isQueueRunning ? 'Generating...' : `Start Queue (${articlesToGenerate.length})`}
                </button>
            </div>
        </div>
    );
};

// --- SUB-COMPONENT: Image Generator Tab ---
const ImageGeneratorTab: React.FC<{ currentUser: UserProfile }> = ({ currentUser }) => {
    const [articles, setArticles] = useState<ArticleForImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [processingType, setProcessingType] = useState<'prompts' | 'images' | null>(null);
    const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);

    const checkedIds = useMemo(() => new Set(articles.filter(a => a.checked).map(a => a.id)), [articles]);

    useEffect(() => {
        const fetchArticles = async () => {
            setLoading(true);
            try {
                const res = await fetch('/api/cms/blog?needs_image=true');
                if (res.ok) {
                    const data = await res.json();
                    setArticles(data.map((a: {id: number, title: string}) => ({
                        id: a.id,
                        title: a.title,
                        prompt: `Stwórz symboliczne zdjęcie dla artykułu o tytule: ${a.title}`,
                        status: 'pending',
                        checked: true
                    })));
                }
            } catch (_e) { console.error(_e); }
            setLoading(false);
        };
        fetchArticles();
    }, []);

    const handleSelectRow = (id: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newArticles = [...articles];
        
        if (e.shiftKey && lastSelectedId !== null) {
            const currentIndex = newArticles.findIndex(a => a.id === id);
            const lastIndex = newArticles.findIndex(a => a.id === lastSelectedId);
            const start = Math.min(currentIndex, lastIndex);
            const end = Math.max(currentIndex, lastIndex);
            const isChecking = !newArticles[currentIndex].checked;
            for (let i = start; i <= end; i++) {
                newArticles[i].checked = isChecking;
            }
        } else {
            const article = newArticles.find(a => a.id === id);
            if (article) article.checked = !article.checked;
        }

        setArticles(newArticles);
        setLastSelectedId(id);
    };

    const handleGeneratePrompts = async () => {
        setProcessingType('prompts');
        const articlesToProcess = articles.filter(a => a.checked);
        
        // Mark all selected as processing
        setArticles(prev => prev.map(a => articlesToProcess.some(p => p.id === a.id) ? { ...a, status: 'generating_prompt' } : a));

        // Process in chunks of 20
        const chunkSize = 20;
        for (let i = 0; i < articlesToProcess.length; i += chunkSize) {
            const chunk = articlesToProcess.slice(i, i + chunkSize);
            const payload = chunk.map(a => ({ id: a.id, title: a.title }));

            try {
                const res = await fetch('/api/cms/generate', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ type: 'image_prompt', articles: payload })
                });
                
                if (!res.ok) throw new Error("API failed");
                const results = await res.json(); // Expecting array of { id, prompt }

                setArticles(prev => prev.map(a => {
                    const result = results.find((r: { id: number; prompt: string }) => r.id === a.id);
                    if (result) {
                        return { ...a, prompt: result.prompt, status: 'pending' };
                    }
                    return a;
                }));

            } catch (e: unknown) {
                console.error(e);
                setArticles(prev => prev.map(a => chunk.some(c => c.id === a.id) ? { ...a, status: 'error', error: 'Batch Prompt failed' } : a));
            }
        }
        setProcessingType(null);
    };

    const handleGenerateImages = async () => {
        setProcessingType('images');
        const articlesToProcess = articles.filter(a => a.checked);

        for (const article of articlesToProcess) {
            setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: 'generating_image' } : a));
            try {
                const res = await fetch('/api/cms/generate-image', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                    body: JSON.stringify({ prompt: article.prompt, articleId: article.id, contextName: article.title })
                });
                if (!res.ok) throw new Error((await res.json()).error || "Image generation failed");

                // On success, remove from list for better UX
                setArticles(prev => prev.filter(a => a.id !== article.id));
            } catch (e: unknown) {
                const message = e instanceof Error ? e.message : 'An unknown error occurred.';
                setArticles(prev => prev.map(a => a.id === article.id ? { ...a, status: 'error', error: message } : a));
            }
        }
        setProcessingType(null);
    };
    
    return (
        <div className="h-full flex flex-col">
            <div className="flex-none flex justify-between items-center mb-4">
                 <p className="text-gray-400 text-sm">Articles without thumbnail ({articles.length})</p>
                 <div className="flex items-center gap-2">
                     <button onClick={handleGeneratePrompts} disabled={processingType !== null || checkedIds.size === 0} className="text-xs font-bold px-4 py-2 bg-mystic-btn text-white rounded-md flex items-center gap-2 disabled:opacity-50">
                         {processingType === 'prompts' ? <Loader2 className="animate-spin" size={14}/> : <Wand2 size={14}/>}
                         Generate Prompts ({checkedIds.size})
                     </button>
                     <button onClick={handleGenerateImages} disabled={processingType !== null || checkedIds.size === 0} className="text-xs font-bold px-4 py-2 bg-mystic-accent text-black rounded-md flex items-center gap-2 disabled:opacity-50">
                         {processingType === 'images' ? <Loader2 className="animate-spin" size={14}/> : <ImageIcon size={14}/>}
                         Generate Images ({checkedIds.size})
                     </button>
                 </div>
            </div>
            <div className="flex-grow overflow-y-auto border border-gray-800 rounded-lg bg-mystic-box">
                {loading ? <Loader2 className="animate-spin mx-auto my-10"/> : (
                    <div className="divide-y divide-gray-800">
                        {articles.map(article => (
                            <div key={article.id} className="p-4 grid grid-cols-12 gap-4 items-start">
                                <div className="col-span-1 flex justify-center pt-2">
                                    <div onClick={(e) => handleSelectRow(article.id, e)} className="cursor-pointer">
                                        {article.checked ? <CheckSquare className="w-5 h-5 text-mystic-accent"/> : <Square className="w-5 h-5 text-gray-600"/>}
                                    </div>
                                </div>
                                <div className="col-span-4">
                                    <p className="font-bold text-white text-sm">{article.title}</p>
                                </div>
                                <div className="col-span-6">
                                    <textarea
                                        value={article.prompt}
                                        onChange={e => setArticles(prev => prev.map(a => a.id === article.id ? { ...a, prompt: e.target.value } : a))}
                                        className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-gray-300 text-xs h-16 resize-none"
                                    />
                                </div>
                                <div className="col-span-1 text-center pt-2">
                                    {/* FIX: Lucide icons do not have a 'title' prop. Wrap in a span with a title attribute. */}
                                    {article.status === 'pending' && <span title="Pending"><div className="w-3 h-3 rounded-full bg-gray-600 mx-auto"/></span>}
                                    {/* FIX: Lucide icons do not have a 'title' prop. Wrap in a span with a title attribute. */}
                                    {article.status === 'generating_prompt' && <span title="Generating prompt..."><Loader2 className="animate-spin text-yellow-500 mx-auto" /></span>}
                                    {/* FIX: Lucide icons do not have a 'title' prop. Wrap in a span with a title attribute. */}
                                    {article.status === 'generating_image' && <span title="Generating image..."><Loader2 className="animate-spin text-blue-500 mx-auto" /></span>}
                                    {/* FIX: Lucide icons do not have a 'title' prop. Wrap in a span with a title attribute. */}
                                    {article.status === 'error' && <span title={article.error}><div className="w-3 h-3 rounded-full bg-red-500 mx-auto"/></span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const AIGenerator: React.FC<Props> = ({ currentUser }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const basePath = '/cms/blog/generate';
    const relativePath = location.pathname.substring(basePath.length);
    const pathSegments = relativePath.split('/').filter(Boolean);
    const subTab = pathSegments[0] || 'topics';
    
    const [topicsToProcess, setTopicsToProcess] = useState<TopicPayload[]>([]);
    
    useEffect(() => {
        if(subTab === 'articles') {
            const storedTopics = sessionStorage.getItem('ai_topics_to_generate');
            if (storedTopics) {
                setTopicsToProcess(JSON.parse(storedTopics));
            }
        }
    }, [subTab]);

    const handleTopicsSelected = (selectedTopics: TopicPayload[]) => {
        if (selectedTopics.length === 0) return;
        const storedTopics = sessionStorage.getItem('ai_topics_to_generate');
        const existingTopics = storedTopics ? JSON.parse(storedTopics) : [];
        const allTopics = [...existingTopics, ...selectedTopics];
        sessionStorage.setItem('ai_topics_to_generate', JSON.stringify(allTopics));
        setTopicsToProcess(allTopics);
        navigate(`${basePath}/articles`);
    };

    const handleBackToTopics = () => {
        navigate(basePath);
    };
    
    const renderSubPage = () => {
        switch (subTab) {
            case 'articles': return <ArticlesGenerator topics={topicsToProcess} currentUser={currentUser} onBack={handleBackToTopics} />;
            case 'images': return <ImageGeneratorTab currentUser={currentUser} />;
            case 'topics':
            default: return <TopicsGenerator onTopicsSelected={handleTopicsSelected} />;
        }
    };

    const navItems = [
        { id: 'topics', label: 'Topics', icon: Wand2 },
        { id: 'articles', label: 'Articles', icon: FileText },
        { id: 'images', label: 'Images', icon: ImageIcon },
    ];

    return (
        <div className="w-full relative animate-fade-in h-[calc(100vh-200px)] flex flex-col">
            <div className="flex-none mb-4 bg-mystic-bg p-1 rounded-lg border border-gray-800 self-start">
                <nav className="flex items-center gap-1">
                    {navItems.map(item => (
                        <button 
                            key={item.id}
                            onClick={() => {
                                if (item.id === 'articles' && topicsToProcess.length === 0) return;
                                navigate(`${basePath}/${item.id}`)
                            }}
                            disabled={item.id === 'articles' && topicsToProcess.length === 0 && subTab !== 'articles'}
                            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${subTab === item.id ? 'bg-gray-700 text-white' : 'text-gray-400 hover:bg-gray-800'} disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            <item.icon className="w-4 h-4" />
                            {item.label}
                        </button>
                    ))}
                </nav>
            </div>

            <div className="flex-grow">
                {renderSubPage()}
            </div>
        </div>
    );
};

export default AIGenerator;